/**
 * JobSummarySection — mutations route through event-workspace-api:
 * sendContractorReminder, notifyContractors, and getEventDetails (poll loop).
 */
import { useState } from "react";
import { SectionCard } from "../ui/SectionCard";
import { Banner } from "../ui/Banner";
import { Button } from "../ui/Button";
import { ConfirmModal } from "../ui/ConfirmModal";
import { formatTimeDisplay, formatTimestamp } from "../../lib/format-utils";
import { formatNZDCompact } from "@shared/nz-money";
import {
  artifactSummaryLine,
  awaitContractorNotification,
  useOptimisticMask,
} from "../../lib/generated-artifacts";
import {
  MUTATION_KEYS,
  type MutationRunner,
} from "../../lib/use-event-workspace";
import { isActive, isRenotifyTarget } from "../../lib/assignment-status";
import {
  getEventDetails,
  notifyContractors,
  sendContractorReminder,
  type WorkspaceAssignment,
} from "../../lib/event-workspace-api";
import { isAvailabilityControlledItem } from "../../lib/booking-item-types";
import mask from "./mask.module.css";
import styles from "./JobSummarySection.module.css";

/** mapsUrl builds a Google Maps search link for an address — mirrors the
 * tappable location link on the contractor job sheet (jobsheet.go). */
function mapsUrl(address: string): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
}

interface GearItem {
  item_name: string;
  quantity: number;
  notes: string | null;
  type?: string;
}

/** The workspace assignment slice this section reads. */
type Assignment = Pick<
  WorkspaceAssignment,
  | "id"
  | "contractorId"
  | "contractor"
  | "status"
  | "hourlyRate"
  | "estimatedHours"
  | "payAmount"
  | "tasksDescription"
  | "reminderSentAt"
>;

interface JobSummarySectionProps {
  bookingId: string;
  booking: {
    event_name: string | null;
    event_date: string;
    event_end_date?: string | null;
    event_time: string | null;
    quote_number?: string | null;
    location: string | null;
    call_time: string | null;
    pack_out_time: string | null;
    site_available_from: string | null;
    call_out_notes: string | null;
    crew_count: number | null;
    // Mirrors the contractor job sheet — see jobsheet.go.
    venue_contact?: string | null;
    client_name?: string | null;
    client_phone?: string | null;
    client_email?: string | null;
    event_type?: string | null;
    attendance?: string | null;
    indoor_outdoor?: string | null;
    content_requirements?: string[];
    has_stage?: boolean;
    stage_details?: string | null;
    power_access?: string | null;
    needs_generator?: boolean;
    wet_weather_plan?: string | null;
    execution_notes?: string[];
  };
  gearItems: GearItem[];
  assignments: Assignment[];
  token: string;
  contractorsNotifiedAt: string | null;
  jobSummaryGeneratedAt: string | null;
  state: "active" | "complete" | "future";
  isStale?: boolean;
  /** Workspace mutation runner — owns busy state, refetch, and the error sink. */
  mutations: MutationRunner;
  open?: boolean;
  onToggle?: () => void;
  sectionRef?: React.RefObject<HTMLDivElement | null>;
  headerAction?: React.ReactNode;
  /** Jump to the Job Details section, where the regenerate button lives. */
  onGoToJobDetails: () => void;
}

export function JobSummarySection({
  bookingId,
  booking,
  gearItems,
  assignments,
  token,
  contractorsNotifiedAt,
  jobSummaryGeneratedAt,
  state,
  isStale = false,
  mutations,
  open,
  onToggle,
  sectionRef,
  headerAction,
  onGoToJobDetails,
}: JobSummarySectionProps) {
  const notifying = mutations.isPending(MUTATION_KEYS.notifyContractors);
  const sendingReminder = mutations.isPending(
    MUTATION_KEYS.sendContractorReminder,
  );
  const [showStaleConfirm, setShowStaleConfirm] = useState(false);
  const [showRenotifyConfirm, setShowRenotifyConfirm] = useState(false);
  const hasBeenGenerated = !!jobSummaryGeneratedAt;
  const { showMask, markSent: markNotified } = useOptimisticMask({
    sentAt: contractorsNotifiedAt,
    generatedAt: jobSummaryGeneratedAt,
  });

  const handleSendReminder = async () => {
    await mutations.run(
      MUTATION_KEYS.sendContractorReminder,
      "Send contractor reminder",
      () => sendContractorReminder(bookingId),
    );
  };

  const handleNotifyContractors = async () => {
    await mutations.run(
      MUTATION_KEYS.notifyContractors,
      "Notify contractors",
      async () => {
        await notifyContractors(token, bookingId, !!contractorsNotifiedAt);
        markNotified();
        // Notification fans out async on the server; wait until an
        // assignment flips or the booking is stamped, then the runner's
        // refetch pulls the final state.
        await awaitContractorNotification(assignments, async () => {
          try {
            return await getEventDetails(bookingId);
          } catch {
            return null;
          }
        });
      },
    );
  };

  const gearOnlyItems = gearItems.filter((g) =>
    isAvailabilityControlledItem(g.type),
  );
  const crewMembers = assignments.filter((a) => a.contractor && isActive(a));
  const renotifyTargets = assignments.filter(
    (a) => a.contractor && isRenotifyTarget(a),
  );

  // Sections mirror the contractor job sheet (jobsheet.go) so the operator
  // sees the same content the contractor will receive.
  const hasSchedule = !!(
    booking.site_available_from ||
    booking.call_time ||
    booking.event_time ||
    booking.pack_out_time
  );
  const hasContacts = !!(
    booking.venue_contact ||
    booking.client_name ||
    booking.client_phone ||
    booking.client_email
  );
  const hasEventDetails = !!(
    booking.event_type ||
    booking.attendance ||
    booking.indoor_outdoor ||
    booking.content_requirements?.length ||
    booking.has_stage ||
    booking.stage_details ||
    booking.power_access ||
    booking.needs_generator ||
    booking.wet_weather_plan
  );
  const clientContact = [booking.client_phone, booking.client_email]
    .filter(Boolean)
    .join("  ·  ");

  const summary = artifactSummaryLine({
    isStale,
    staleLine: "Job summary is out of date, regenerate from Job Details above",
    showMask,
    maskLine: "Contractors notified, regenerate to update",
    contentLine: hasBeenGenerated
      ? `${crewMembers.length} contractor${crewMembers.length !== 1 ? "s" : ""}, ${gearOnlyItems.length} gear items`
      : null,
  });

  return (
    <SectionCard
      title="Job Summary"
      collapsible
      open={open}
      onToggle={onToggle}
      sectionRef={sectionRef}
      state={state}
      isStale={isStale}
      summary={summary}
      headerAction={headerAction}
    >
      {isStale && (
        <Banner variant="warning" className={styles.staleBanner}>
          This job summary is out of date.{" "}
          <button
            type="button"
            onClick={onGoToJobDetails}
            className={styles.bannerLink}
          >
            Open Job Details
          </button>{" "}
          and re-generate the job summary to update it.
        </Banner>
      )}
      <div className={mask.masked}>
        {showMask && (
          <div className={mask.mask}>
            <p className={mask.text}>
              Contractors have been notified with this job summary.
              <br />
              To send updated details, edit Job Details above and click
              &quot;Generate Job Summary&quot;.
            </p>
            {(() => {
              const lastReminder = assignments
                .filter((a) => a.status === "notified" && a.reminderSentAt)
                .map((a) => new Date(a.reminderSentAt!).getTime())
                .sort((a, b) => b - a)[0];
              const latestDate = lastReminder
                ? formatTimestamp(new Date(lastReminder).toISOString())
                : contractorsNotifiedAt
                  ? formatTimestamp(contractorsNotifiedAt)
                  : null;
              const label = lastReminder ? "Reminder sent" : "Notified";
              return latestDate ? (
                <p className={mask.sentAt}>
                  {label} {latestDate}
                </p>
              ) : null;
            })()}
            {assignments.some((a) => a.status === "notified") && (
              <Button
                onClick={handleSendReminder}
                variant="outline"
                loading={sendingReminder}
              >
                Send Contractor Reminder
              </Button>
            )}
          </div>
        )}

        {!hasBeenGenerated ? (
          <div className={styles.empty}>
            <p>No job summary generated yet.</p>
            <p className={styles.emptyHint}>
              Fill in Job Details and assign crew, then generate the summary.
            </p>
          </div>
        ) : (
          <div className={styles.sheet}>
            {/* Event header */}
            <div className={styles.eventHeader}>
              <p className={styles.eventName}>{booking.event_name || "Event"}</p>
              <p className={styles.eventMeta}>
                {booking.event_date}
                {booking.event_end_date &&
                booking.event_end_date !== booking.event_date
                  ? ` to ${booking.event_end_date}`
                  : ""}
                {booking.event_time ? ` · ${booking.event_time}` : ""}
                {booking.quote_number ? ` · Quote ${booking.quote_number}` : ""}
              </p>
            </div>

            {/* Vitals — call time + location (tappable map), like the job sheet */}
            <div className={styles.vitals}>
              <div>
                <p className={styles.vitalLabel}>Call Time</p>
                <p className={styles.vitalValue}>
                  {booking.call_time
                    ? formatTimeDisplay(booking.call_time)
                    : "TBC"}
                </p>
              </div>
              <div>
                <p className={styles.vitalLabel}>Location</p>
                {booking.location ? (
                  <a
                    href={mapsUrl(booking.location)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.mapLink}
                  >
                    {booking.location}
                  </a>
                ) : (
                  <p className={styles.vitalValue}>TBC</p>
                )}
              </div>
            </div>

            {/* Schedule */}
            {hasSchedule && (
              <div className={styles.schedule}>
                <p className={styles.scheduleTitle}>Schedule</p>
                {booking.site_available_from && (
                  <p>
                    <span className={styles.fieldLabel}>Site Open:</span>{" "}
                    {formatTimeDisplay(booking.site_available_from)}
                  </p>
                )}
                {booking.call_time && (
                  <p>
                    <span className={styles.fieldLabel}>Call:</span>{" "}
                    {formatTimeDisplay(booking.call_time)}
                  </p>
                )}
                {booking.event_time && (
                  <p>
                    <span className={styles.fieldLabel}>Event:</span>{" "}
                    {booking.event_time}
                  </p>
                )}
                {booking.pack_out_time && (
                  <p>
                    <span className={styles.fieldLabel}>Pack Out by:</span>{" "}
                    {formatTimeDisplay(booking.pack_out_time)}
                  </p>
                )}
              </div>
            )}

            {/* Access & on-site notes */}
            {booking.call_out_notes && (
              <div>
                <p className={styles.notesTitle}>
                  Access &amp; On-Site Notes
                </p>
                <p className={styles.notesBody}>
                  {booking.call_out_notes}
                </p>
              </div>
            )}

            {/* Who to call */}
            {hasContacts && (
              <div>
                <p className={styles.blockTitle}>Who to Call</p>
                <div className={styles.contacts}>
                  {booking.venue_contact && (
                    <div>
                      <p className={styles.contactLabel}>Venue / Site</p>
                      <p className={styles.contactValue}>
                        {booking.venue_contact}
                      </p>
                    </div>
                  )}
                  {(booking.client_name || clientContact) && (
                    <div>
                      <p className={styles.contactLabel}>Client</p>
                      {booking.client_name && (
                        <p className={styles.contactName}>
                          {booking.client_name}
                        </p>
                      )}
                      {clientContact && (
                        <p className={styles.contactMeta}>
                          {clientContact}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {gearOnlyItems.length > 0 && (
              <div>
                <p className={styles.blockTitle}>
                  Gear ({gearOnlyItems.length} items)
                </p>
                <div className={styles.list}>
                  {gearOnlyItems.map((item, i) => (
                    <p key={i}>
                      {item.quantity}x {item.item_name}
                      {item.notes ? `, ${item.notes}` : ""}
                    </p>
                  ))}
                </div>
              </div>
            )}

            {crewMembers.length > 0 && (
              <div>
                <p className={styles.blockTitle}>
                  Crew ({crewMembers.length})
                </p>
                <div className={styles.crewList}>
                  {crewMembers.map((a) => (
                    <div
                      key={a.id}
                      className={styles.crewCard}
                    >
                      <div className={styles.crewRow}>
                        <span className={styles.crewName}>
                          {a.contractor?.name || "Unknown"}
                        </span>
                        <span className={styles.crewPay}>
                          {a.hourlyRate && a.estimatedHours
                            ? `$${a.hourlyRate}/hr × ${a.estimatedHours}hrs = ${formatNZDCompact(a.payAmount || 0)}`
                            : formatNZDCompact(a.payAmount || 0)}
                        </span>
                      </div>
                      {a.tasksDescription && (
                        <p className={styles.crewTasks}>
                          {a.tasksDescription}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {booking.execution_notes && booking.execution_notes.length > 0 && (
              <div>
                <p className={styles.blockTitle}>Setup Notes</p>
                <div className={styles.list}>
                  {booking.execution_notes.map((n, i) => (
                    <p key={i}>• {n}</p>
                  ))}
                </div>
              </div>
            )}

            {hasEventDetails && (
              <div>
                <p className={styles.blockTitle}>Event Details</p>
                <div className={styles.list}>
                  {booking.event_type && (
                    <p>
                      <span className={styles.fieldLabel}>Type:</span>{" "}
                      {booking.event_type}
                    </p>
                  )}
                  {booking.attendance && (
                    <p>
                      <span className={styles.fieldLabel}>Attendance:</span>{" "}
                      {booking.attendance}
                    </p>
                  )}
                  {booking.indoor_outdoor && (
                    <p>
                      <span className={styles.fieldLabel}>Venue:</span>{" "}
                      {booking.indoor_outdoor}
                    </p>
                  )}
                  {booking.content_requirements &&
                    booking.content_requirements.length > 0 && (
                      <p>
                        <span className={styles.fieldLabel}>Content:</span>{" "}
                        {booking.content_requirements.join(", ")}
                      </p>
                    )}
                  {(booking.has_stage || booking.stage_details) && (
                    <p>
                      <span className={styles.fieldLabel}>Stage:</span>{" "}
                      {booking.stage_details ||
                        (booking.has_stage ? "Yes" : "No")}
                    </p>
                  )}
                  {booking.power_access && (
                    <p>
                      <span className={styles.fieldLabel}>Power Access:</span>{" "}
                      {booking.power_access}
                    </p>
                  )}
                  {booking.needs_generator && (
                    <p>
                      <span className={styles.fieldLabel}>Generator:</span>{" "}
                      Required
                    </p>
                  )}
                  {booking.wet_weather_plan && (
                    <p>
                      <span className={styles.fieldLabel}>Wet Weather:</span>{" "}
                      {booking.wet_weather_plan}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {hasBeenGenerated && !showMask && (
        <div className={styles.footer}>
          {contractorsNotifiedAt ? (
            <Button
              onClick={() =>
                isStale
                  ? setShowStaleConfirm(true)
                  : setShowRenotifyConfirm(true)
              }
              loading={notifying}
              disabled={renotifyTargets.length === 0}
              className={styles.fullWidth}
            >
              {`Renotify ${renotifyTargets.length} Contractor${renotifyTargets.length !== 1 ? "s" : ""}`}
            </Button>
          ) : (
            <Button
              onClick={() =>
                isStale ? setShowStaleConfirm(true) : handleNotifyContractors()
              }
              loading={notifying}
              disabled={crewMembers.length === 0}
              className={styles.fullWidth}
            >
              {`Notify ${crewMembers.length} Contractor${crewMembers.length !== 1 ? "s" : ""}`}
            </Button>
          )}
        </div>
      )}
      <ConfirmModal
        open={showStaleConfirm}
        title="Job summary is out of date"
        message={
          <>
            You&apos;ve edited job details, gear, or crew since this job sheet
            was generated. Contractors would receive the <strong>old</strong>{" "}
            sheet. Regenerate the job summary first?
          </>
        }
        confirmLabel="Notify anyway"
        confirmVariant="danger"
        loading={notifying}
        onConfirm={() => {
          const targets = contractorsNotifiedAt ? renotifyTargets : crewMembers;
          if (notifying || targets.length === 0) return;
          setShowStaleConfirm(false);
          if (contractorsNotifiedAt) {
            setShowRenotifyConfirm(true);
            return;
          }
          void handleNotifyContractors();
        }}
        onCancel={() => {
          if (!notifying) setShowStaleConfirm(false);
        }}
      />
      <ConfirmModal
        open={showRenotifyConfirm}
        title="Contractors will need to confirm again"
        message={
          <>
            All <strong>{renotifyTargets.length}</strong> active contractors
            will be asked to confirm acceptance against the updated job sheet,
            including anyone who previously accepted or declined.
            <br />
            <br />
            Their previous responses will be cleared.
          </>
        }
        confirmLabel="Renotify and reset responses"
        confirmVariant="danger"
        loading={notifying}
        onConfirm={() => {
          if (notifying || renotifyTargets.length === 0) return;
          setShowRenotifyConfirm(false);
          void handleNotifyContractors();
        }}
        onCancel={() => {
          if (!notifying) setShowRenotifyConfirm(false);
        }}
      />
    </SectionCard>
  );
}
